import React, { useState, useEffect } from 'react';
import { Settings, Clock, Play, FileText, History, Zap, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AutomationControl = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [editSettings, setEditSettings] = useState({});

  // Redirect if not superadmin
  useEffect(() => {
    // Wait for user to load first
    if (user) {
      if (user.role !== 'super_admin') {
        toast.error('Access denied. Only superadmin can access Automation.');
        navigate('/app/dashboard');
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch jobs, audit log, and settings in parallel
      const [jobsRes, auditRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/automation/jobs`, { headers }),
        axios.get(`${API_URL}/api/automation/audit-log?limit=20`, { headers }),
        axios.get(`${API_URL}/api/automation/settings`, { headers })
      ]);

      if (jobsRes.data.success) {
        setJobs(jobsRes.data.data || []);
      }

      if (auditRes.data.success) {
        setAuditLog(auditRes.data.data || []);
      }

      if (settingsRes.data.success) {
        setSettings(settingsRes.data.data || {});
        setEditSettings(settingsRes.data.data || {});
      }
    } catch (error) {
      toast.error('Gagal memuat data automation');
      console.error(error);
    }
  };

  const handleManualTrigger = async (jobType) => {
    setIsLoading(true);
    try {
      let endpoint = '';
      let jobName = '';

      switch(jobType) {
        case 'isolir':
          endpoint = '/api/automation/isolir-overdue';
          jobName = 'Auto-Isolir';
          break;
        case 'unisolir':
          endpoint = '/api/automation/unisolir-paid';
          jobName = 'Auto-Unisolir';
          break;
        case 'invoice':
          endpoint = '/api/automation/generate-invoices';
          jobName = 'Generate Invoices';
          break;
        default:
          return;
      }

      const response = await axios.post(`${API_URL}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`${jobName} berhasil dijalankan!`);
        fetchData(); // Refresh audit log
      } else {
        toast.error(response.data.message || `Gagal menjalankan ${jobName}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Terjadi kesalahan');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Save each changed setting
      const promises = Object.keys(editSettings).map(async (key) => {
        if (editSettings[key] !== settings[key]) {
          return axios.post(`${API_URL}/api/automation/settings`, null, {
            headers,
            params: {
              setting_name: key,
              value: editSettings[key]
            }
          });
        }
      });

      await Promise.all(promises.filter(p => p));
      
      toast.success('Settings berhasil disimpan');
      fetchData();
    } catch (error) {
      toast.error('Gagal menyimpan settings');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Show loading while checking user
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not superadmin (redirect will happen)
  if (user.role !== 'super_admin') {
    return null;
  }

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
            <Zap className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            Automation Control
          </h1>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Kelola dan monitor automation jobs, settings, dan history
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scheduled Jobs */}
        <Card className="lg:col-span-2 border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Scheduled Jobs
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {jobs.length === 0 ? (
              <p className="text-center text-slate-500 py-8 dark:text-slate-400">Tidak ada scheduled jobs</p>
            ) : (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div key={job.id} className="border border-slate-200 rounded-lg p-4 dark:border-slate-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800 dark:text-slate-200">{job.name}</h3>
                        <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">ID: {job.id}</p>
                        <div className="mt-3 space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                            <span className="text-slate-600 dark:text-slate-300">
                              Next Run: {formatDate(job.next_run_time)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <span>Trigger: {job.trigger}</span>
                          </div>
                        </div>
                      </div>
                      <Badge className={`${job.next_run_time ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {job.next_run_time ? 'Active' : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
            <CardTitle className="text-lg flex items-center gap-2">
              <Play className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <Button
                onClick={() => handleManualTrigger('isolir')}
                disabled={isLoading}
                className="w-full justify-start bg-orange-600 hover:bg-orange-700"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Run Auto-Isolir
              </Button>

              <Button
                onClick={() => handleManualTrigger('unisolir')}
                disabled={isLoading}
                className="w-full justify-start bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Run Auto-Unisolir
              </Button>

              <Button
                onClick={() => handleManualTrigger('invoice')}
                disabled={isLoading}
                className="w-full justify-start bg-indigo-600 hover:bg-indigo-700"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generate Invoices
              </Button>

              <div className="pt-3 border-t">
                <p className="text-xs text-slate-500 mb-2 dark:text-slate-400">
                  💡 Manual trigger untuk testing atau emergency
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Automation Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 dark:text-slate-200">
                Grace Period (Isolir)
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={editSettings.auto_isolir_grace_period || 3}
                  onChange={(e) => setEditSettings({
                    ...editSettings,
                    auto_isolir_grace_period: parseInt(e.target.value)
                  })}
                  className="w-20"
                />
                <span className="text-sm text-slate-600 dark:text-slate-300">hari</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                Jangka waktu sebelum auto-isolir
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 dark:text-slate-200">
                Invoice Due Days
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={editSettings.invoice_due_days || 15}
                  onChange={(e) => setEditSettings({
                    ...editSettings,
                    invoice_due_days: parseInt(e.target.value)
                  })}
                  className="w-20"
                />
                <span className="text-sm text-slate-600 dark:text-slate-300">hari</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                Jatuh tempo invoice dari issue date
              </p>
            </div>

            <div className="flex items-end">
              <Button
                onClick={handleSaveSettings}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Automation History
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b dark:bg-slate-900">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Timestamp</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Action</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center py-8 text-slate-500 dark:text-slate-400">
                      Belum ada history
                    </td>
                  </tr>
                ) : (
                  auditLog.map((log, index) => (
                    <tr key={index} className="border-b hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-xs">
                          {log.action}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                        {log.details?.isolated_count !== undefined && (
                          <span>Isolated: {log.details.isolated_count}, Failed: {log.details.failed_count || 0}</span>
                        )}
                        {log.details?.unisolated_count !== undefined && (
                          <span>Unisolated: {log.details.unisolated_count}</span>
                        )}
                        {log.details?.generated_count !== undefined && (
                          <span>Generated: {log.details.generated_count}, Skipped: {log.details.skipped_count || 0}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomationControl;
